import is from '@sindresorhus/is';
import type { FunctionComponent, HTMLAttributes, MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { TranslationKey } from '~/shared/internationalization/translation';

import { useSnackbars } from '../app/error-handling/SnackBars';
import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import { COMMENT_ID_ATTRIBUTE, commentMarkSelector } from './anchor/grammar';
import { useCommentableAnchor } from './anchor/use-commentable-anchor';
import { SelectionCommentButton } from './SelectionCommentButton';
import { SectionContent } from './section-content';
import { stripCommentMarks } from './strip-comment-marks';
import { useResolvedSectionMarkedHtml } from './use-resolved-section-marked-html';
import { useTextSelection } from './use-text-selection';
import type { WrapSelectionResult } from './wrap-selection-in-mark';
import { wrapSelectionInMark } from './wrap-selection-in-mark';

const wrapSelectionFailureToTranslationKey = (
    reason: Extract<WrapSelectionResult, { readonly ok: false }>['reason'],
): TranslationKey => {
    switch (reason) {
        case 'cross-section':
            return 'pages.job-architecture-v2.comments.errors.cross-section';
        case 'collapsed':
        case 'empty':
            return 'pages.job-architecture-v2.comments.errors.select-text-first';
    }
};

type SectionTitleVariant = 'section' | 'label';

const titleVariantClassNames: Record<SectionTitleVariant, string> = {
    section: 'mb-xl text-body-s font-medium capitalize leading-normal text-text-primary',
    label: 'mb-xs text-body-xs font-medium text-text-content-subtle',
};

const TitleText: FunctionComponent<{ readonly children: string; readonly variant?: SectionTitleVariant }> = ({
    children,
    variant = 'section',
}) => <div className={titleVariantClassNames[variant]}>{children}</div>;

type CommentableDescriptionSectionProps = {
    readonly contentClassName?: string;
    readonly itemId: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly sectionKey: string;
    /**
     * Work-task comments address the task body with a single `description` target; job-profile
     * item comments address a named section. Defaults to the job-profile vocabulary.
     */
    readonly targetKind?: 'description' | 'description-section';
    /** Omit to render the section content without a visible heading. */
    readonly sectionTitle?: string;
    readonly titleVariant?: SectionTitleVariant;
    readonly html: string;
};

export const CommentableDescriptionSection: FunctionComponent<CommentableDescriptionSectionProps> = ({
    contentClassName,
    itemId,
    newThreadCreationDisabled = false,
    sectionKey,
    sectionTitle,
    targetKind = 'description-section',
    titleVariant = 'section',
    html,
}) => {
    const sectionRef = useRef<HTMLElement | null>(null);
    const setActiveThreadId = useActiveThreadStore((state) => state.setActiveThreadId);
    const setViewMode = useActiveThreadStore((state) => state.setViewMode);
    const { attrs, openThread, startComment } = useCommentableAnchor(
        targetKind === 'description'
            ? { kind: 'description', itemId }
            : { kind: 'description-section', itemId, sectionId: sectionKey },
    );
    const { selection, clearSelection } = useTextSelection(sectionRef);
    const { showErrorMessageSnackbar } = useSnackbars();
    const markedHtml = useResolvedSectionMarkedHtml(sectionKey, html);

    useEffect(() => {
        if (!is.null(selection)) {
            setActiveThreadId(null);
            setViewMode('active');
        }
    }, [selection, setActiveThreadId, setViewMode]);

    const innerHtmlProp = useMemo(() => ({ __html: markedHtml ?? html }), [html, markedHtml]);

    const handleClick = useCallback(
        (event: MouseEvent<HTMLDivElement>): void => {
            const target = event.target instanceof HTMLElement ? event.target : null;
            if (is.null(target)) return;
            const mark = target.closest<HTMLElement>(commentMarkSelector());
            if (is.null(mark)) return;
            const commentId = mark.getAttribute(COMMENT_ID_ATTRIBUTE);
            if (is.null(commentId)) return;
            openThread(commentId);
        },
        [openThread],
    );

    const handleComment = (): void => {
        if (is.null(selection)) {
            showErrorMessageSnackbar(wrapSelectionFailureToTranslationKey('collapsed'));
            return;
        }
        const range = selection.getRange();
        if (is.null(range)) {
            showErrorMessageSnackbar(wrapSelectionFailureToTranslationKey('collapsed'));
            return;
        }
        const anchorRect = range.getBoundingClientRect();
        const sectionEl = sectionRef.current;
        if (is.null(sectionEl)) return;
        const sectionRoot = sectionEl.querySelector<HTMLElement>('[data-section-content-root="true"]');
        if (is.null(sectionRoot)) return;
        const id = crypto.randomUUID();
        const result = wrapSelectionInMark({ range, sectionRoot });
        if (!result.ok) {
            showErrorMessageSnackbar(wrapSelectionFailureToTranslationKey(result.reason));
            return;
        }
        startComment(sectionEl, { anchorRect, anchor: result.anchor, commentId: id });
        clearSelection();
    };

    return (
        <section
            ref={sectionRef}
            {...attrs}
            data-section-key={sectionKey}
            data-testid="commentable-description-section"
        >
            {sectionTitle === undefined ? null : <TitleText variant={titleVariant}>{sectionTitle}</TitleText>}
            <SectionContent
                className={contentClassName}
                data-section-content-root="true"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: rich content with inline marks
                dangerouslySetInnerHTML={innerHtmlProp}
                data-testid="description-section-content"
                onClick={handleClick}
            />
            {is.null(selection) || newThreadCreationDisabled ? null : (
                <SelectionCommentButton rect={selection.rect} onComment={handleComment} />
            )}
        </section>
    );
};

type PlainDescriptionSectionProps = {
    /** Props for the content element, e.g. a click handler for comment marks. */
    readonly contentProps?: HTMLAttributes<HTMLDivElement>;
    /** Omit to render the section content without a visible heading. */
    readonly sectionTitle?: string;
    readonly html: string;
    /** The html with comment marks to show instead; without it marks are stripped. */
    readonly markedHtml?: string;
    readonly titleVariant?: SectionTitleVariant;
};

export const PlainDescriptionSection: FunctionComponent<PlainDescriptionSectionProps> = ({
    contentProps,
    sectionTitle,
    html,
    markedHtml,
    titleVariant = 'section',
}) => {
    const innerHtmlProp = useMemo(() => ({ __html: markedHtml ?? stripCommentMarks(html) }), [html, markedHtml]);

    return (
        <>
            {sectionTitle === undefined ? null : <TitleText variant={titleVariant}>{sectionTitle}</TitleText>}
            <SectionContent
                {...contentProps}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: rich content rendered in non-commentable contexts
                dangerouslySetInnerHTML={innerHtmlProp}
                data-testid="description-section-content"
            />
        </>
    );
};
